// Lay in a whole job's crew in one go, then fine-tune with cell editing.
//
// PM request, 2026-07-25: "enter the manpower for the entire job from one menu
// with the ability to enter all the information at once. Then we could use the
// cell editing feature for adjustments after the initial setup is complete."
//
// Conventions:
//   • WORKING DAYS ARE CHOSEN, not assumed. The first cut hardcoded Mon–Fri;
//     PMs came straight back (2026-07-27) pointing out that jobs run 4, 5, 6
//     and 7 day weeks, so the days of the week are selectable. Mon–Fri is only
//     the default.
//   • The date range defaults to the job's phase span, falling back to its
//     start date, because that's the answer the scheduler almost always wants.
//   • Everything stages as a DRAFT. Nothing is visible to PMs until Publish,
//     and Reset changes undoes the whole setup.
//
// Overwrite behaviour (product owner, 2026-07-28): the setup is AUTHORITATIVE for the
// job's future. It doesn't just fill the days it writes — it clears any other
// crew already on those rows from today forward, so a Saturday left over from an
// earlier Mon–Sat setup doesn't survive a Mon–Thu one.
//
// "Clear anything that hasn't already occurred" — and, from 2026-07-28, the same
// boundary applies to WRITING: the setup schedules from today forward and never
// writes into the past either. That matters because the range DEFAULTS to the
// job's phase span, so a job that started in June opens with a June start date;
// without it, setting up a job already under way would overwrite months of
// worked history with today's crew figure.
//
// Days before today are the record of what was planned at the time and stay put.
// Same rule the "Remove from Manpower" button already uses (from today forward,
// past weeks kept), so the two behave alike.
//
// Clearing is a crew_count of 0, which the draft layer treats as "blank this
// cell". Zeros written over cells that were already empty are dropped by the
// API's draft cleanup, so this can't inflate the unpublished-changes count.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { manpowerApi } from '@/lib/apiClient';
import { addDays, localYmd } from './lib/gridDates';
import { planSetupCells } from './lib/setupPlan';
import {
  buildCrewRows, type ProjectRow, type CrewPhase,
} from './lib/scheduleModel';

/** Day-of-week numbers as Date.getDay() returns them, Sunday = 0. Rendered
 *  Monday-first, which is how a construction week is read. */
const DOW = [
  { n: 1, short: 'M',  label: 'Monday' },
  { n: 2, short: 'T',  label: 'Tuesday' },
  { n: 3, short: 'W',  label: 'Wednesday' },
  { n: 4, short: 'Th', label: 'Thursday' },
  { n: 5, short: 'F',  label: 'Friday' },
  { n: 6, short: 'Sa', label: 'Saturday' },
  { n: 0, short: 'Su', label: 'Sunday' },
] as const;

/** Common week shapes — the 4/5/6/7-day cases PMs actually schedule. */
const DOW_PRESETS: { label: string; dows: number[] }[] = [
  { label: 'Mon–Thu',  dows: [1, 2, 3, 4] },
  { label: 'Mon–Fri',  dows: [1, 2, 3, 4, 5] },
  { label: 'Mon–Sat',  dows: [1, 2, 3, 4, 5, 6] },
  { label: 'All 7',    dows: [1, 2, 3, 4, 5, 6, 0] },
];

/** Dates between two YYYY-MM-DD bounds (inclusive) falling on a chosen day of
 *  the week. */
function daysBetween(start: string, end: string, dows: Set<number>): string[] {
  if (!start || !end || start > end || dows.size === 0) return [];
  const out: string[] = [];
  let d = new Date(start + 'T12:00:00');
  const last = new Date(end + 'T12:00:00');
  // Hard stop well above any legitimate job length — a fat-fingered year would
  // otherwise spin here before the entry cap could reject it.
  let guard = 0;
  while (d <= last && guard++ < 4000) {
    if (dows.has(d.getDay())) out.push(localYmd(d));
    d = addDays(d, 1);
  }
  return out;
}

/** Matches BulkDraftEntriesDto's ArrayMaxSize on the API. */
const MAX_ENTRIES = 1500;

/** How far past today to look for crew this setup should clear. Generous enough
 *  to cover a stale tail from a previous, longer setup on any real job; bounded
 *  so the lookup can't become unbounded. */
const FUTURE_HORIZON_DAYS = 365 * 3;

export interface CrewSetupResult {
  entries: { workDate: string; projectId: number; crewCount: number; phase?: CrewPhase }[];
  /** For the confirmation message. */
  dayCount: number;
  /** Cells that already had crew and are being written over. */
  replacedCount: number;
  /** Future cells being blanked because the new pattern doesn't cover them. */
  clearedCount: number;
}

export default function CrewSetupModal({
  project, windowStart, todayYmd, busy, onClose, onApply,
}: {
  project: ProjectRow;
  /** First date on the visible grid — only used as a last-resort default
   *  when the job has neither phase dates nor a start date. */
  windowStart: string;
  /**
   * The write cutoff, as YYYY-MM-DD: nothing on or before it is touched.
   *
   * ⚠️ PASSED IN, NOT DERIVED, and REQUIRED rather than defaulted. This
   * used to be `todayInputValue()` — the BROWSER's calendar date — while
   * ManpowerPage moved to the Eastern business day for #477. The two agreed
   * only for a viewer already in Eastern. West of it, between midnight
   * Eastern and midnight local, this modal would happily bulk-write the
   * previous Eastern business day that the grid behind it was already drawing
   * as history; east of it, the current business day was skipped instead
   * (Codex on #506).
   *
   * That made it the one SCHEDULE-WRITING path on a different clock from the
   * other two — the shift action and "Remove from Manpower" both take the
   * page's `today`. A default here would let a future caller reintroduce the
   * split silently, which is exactly how it happened the first time.
   */
  todayYmd: string;
  busy: boolean;
  onClose: () => void;
  onApply: (result: CrewSetupResult) => void;
}) {
  const crewRows = useMemo(() => buildCrewRows(project), [project]);
  const isFab = crewRows.length > 1;

  // Default the range to the job's own dates: the widest phase span if it has
  // one, else its start date. Scheduler can override either end.
  const defaults = useMemo(() => {
    const spans = crewRows.map((r) => r.phaseSpan).filter((s): s is { start: string; end: string } => !!s);
    if (spans.length) {
      return {
        start: spans.reduce((a, s) => (s.start < a ? s.start : a), spans[0].start),
        end:   spans.reduce((a, s) => (s.end   > a ? s.end   : a), spans[0].end),
      };
    }
    const start = project.currentStartDate?.slice(0, 10) ?? windowStart;
    return { start, end: '' };
  }, [crewRows, project.currentStartDate, windowStart]);

  const [start, setStart] = useState(defaults.start);
  const [end, setEnd] = useState(defaults.end);
  // One crew figure per line: a single "crew" for most jobs, fabrication and
  // installation separately for a fab job.
  const [counts, setCounts] = useState<Record<string, string>>(
    () => Object.fromEntries(crewRows.map((r) => [r.key, ''])),
  );

  // Which days of the week this job works. Mon–Fri is the default because it's
  // the common case, but a 4, 6 or 7-day job just changes the selection.
  const [dows, setDows] = useState<Set<number>>(() => new Set([1, 2, 3, 4, 5]));
  const toggleDow = (n: number) =>
    setDows((prev) => {
      const next = new Set(prev);
      if (next.has(n)) next.delete(n); else next.add(n);
      return next;
    });
  const dowsMatch = (preset: number[]) =>
    preset.length === dows.size && preset.every((n) => dows.has(n));

  const days = useMemo(() => daysBetween(start, end, dows), [start, end, dows]);

  // Existing crew, fetched per project rather than read off the grid — the
  // schedule feed is windowed, so reading it locally could only ever see the
  // days already on screen and would under-report on a job running past them.
  // Draft overlay on, so the numbers match what the scheduler is looking at in
  // edit mode.
  //
  // The window deliberately spans BEYOND the chosen range: the setup clears the
  // job's whole future, so it has to know about crew sitting after the range end
  // (a stale tail from a previous, longer setup) as well as inside it.
  const rangeValid = !!start && !!end && start <= end;
  const today = todayYmd;
  const lookupFrom = start && start < today ? start : today;
  const lookupTo = useMemo(() => {
    const horizon = localYmd(addDays(new Date(today + 'T12:00:00'), FUTURE_HORIZON_DAYS));
    return end && end > horizon ? end : horizon;
  }, [today, end]);

  const existing = useQuery({
    queryKey: ['crew-setup-existing', project.projectId, lookupFrom, lookupTo],
    queryFn: () => manpowerApi.projectCrewDays(project.projectId, lookupFrom, lookupTo, true),
    enabled: rangeValid,
    staleTime: 0,
  });

  const entered = crewRows
    .map((r) => ({ row: r, n: Number(counts[r.key]) }))
    .filter((x) => counts[x.row.key] !== '' && Number.isFinite(x.n) && x.n >= 0);

  const enteredPhases = entered.map((e) => e.row.phase ?? null);
  const plan = useMemo(
    () => planSetupCells({
      days,
      enteredPhases,
      existing: existing.data ?? [],
      today,
      isFab,
    }),
    // enteredPhases is rebuilt each render; key on its content, not identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days, enteredPhases.join('|'), existing.data, today, isFab],
  );
  const clearedCells = plan.cleared;
  const replacedCount = plan.replaced;
  // Days actually written to — the range's working days minus anything already
  // in the past. Every count shown and every entry sent uses this, never the
  // raw `days`, so the dialog can't promise more than it writes.
  const writeDays = plan.effectiveDays;

  const totalEntries = plan.written.size + clearedCells.length;
  const tooMany = totalEntries > MAX_ENTRIES;

  const problem =
    !start || !end ? 'Pick a start and end date.'
    : start > end ? 'The end date is before the start date.'
    : dows.size === 0 ? 'Pick at least one working day.'
    : days.length === 0 ? 'No selected working days fall inside that range.'
    // Range wholly in the past. Distinguished from "no working days" because
    // the fix is different: move the end date, not the day selection.
    : writeDays.length === 0 ? 'That range has already passed — the setup only schedules from today onward.'
    : entered.length === 0 ? `Enter a crew size${isFab ? ' for at least one phase' : ''}.`
    : tooMany ? `That range needs ${totalEntries.toLocaleString()} entries — over the ${MAX_ENTRIES.toLocaleString()} limit. Set it up in shorter stretches.`
    : null;

  const apply = () => {
    if (problem) return;
    const entries = entered.flatMap(({ row, n }) =>
      writeDays.map((ds) => ({
        workDate: ds,
        projectId: project.projectId,
        crewCount: n,
        ...(row.phase ? { phase: row.phase } : {}),
      })),
    );
    // Blank the future cells the new pattern doesn't cover. crew_count 0 is the
    // draft layer's "empty this cell"; the phase comes back off the key so a fab
    // job clears the right line.
    for (const key of clearedCells) {
      const sep = key.lastIndexOf('|');
      const workDate = key.slice(0, sep);
      // Narrowed to the literal phases rather than cast: CrewPhase includes
      // null, and a null phase must be sent as an ABSENT field (that's how a
      // non-fab job's single row is addressed), not as phase: null.
      const phase = key.slice(sep + 1);
      entries.push({
        workDate,
        projectId: project.projectId,
        crewCount: 0,
        ...(phase === 'fabrication' || phase === 'installation' ? { phase } : {}),
      });
    }
    onApply({
      entries, dayCount: writeDays.length, replacedCount, clearedCount: clearedCells.length,
    });
  };

  const inp: React.CSSProperties = {
    border: '1px solid var(--ew-n-200)', borderRadius: 'var(--ew-r-sm)',
    padding: '5px 8px', fontSize: 13, fontFamily: 'var(--ew-font)',
    color: 'var(--ew-dark)', background: 'var(--ew-white)',
  };
  const lbl: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.06em', color: 'var(--ew-n-500)', marginBottom: 4, display: 'block',
  };

  return (
    <div
      role="dialog" aria-modal="true" aria-label={`Set up crew for ${project.jobNumber}`}
      style={{ position: 'fixed', inset: 0, background: 'rgba(30,30,30,0.45)', zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div style={{ background: 'var(--ew-white)', borderRadius: 'var(--ew-r-md)', boxShadow: 'var(--ew-shadow-3)', width: 'min(520px, 96vw)', maxHeight: '92vh', overflow: 'auto', padding: 20 }}>
        <div style={{ marginBottom: 4, fontSize: 15, fontWeight: 700, color: 'var(--ew-dark)' }}>
          Set up crew
        </div>
        <div style={{ marginBottom: 16, fontSize: 12, color: 'var(--ew-n-500)' }}>
          {project.jobNumber} · {project.title}
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={lbl} htmlFor="crew-setup-start">Start</label>
            <input id="crew-setup-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} style={{ ...inp, width: '100%' }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={lbl} htmlFor="crew-setup-end">End</label>
            <input id="crew-setup-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} style={{ ...inp, width: '100%' }} />
          </div>
        </div>

        {/* Working days. A job might run 4, 5, 6 or 7 days a week, so this is
            chosen rather than assumed — Mon–Fri is just the starting point. */}
        <div style={{ marginBottom: 14 }}>
          <span style={lbl}>Working days</span>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
            {DOW.map((d) => {
              const on = dows.has(d.n);
              return (
                <button
                  key={d.n}
                  type="button"
                  onClick={() => toggleDow(d.n)}
                  aria-pressed={on}
                  aria-label={d.label}
                  title={d.label}
                  style={{
                    minWidth: 38, padding: '5px 6px', fontSize: 12, fontWeight: 700,
                    cursor: 'pointer', borderRadius: 'var(--ew-r-sm)',
                    border: `1px solid ${on ? 'var(--ew-green)' : 'var(--ew-n-200)'}`,
                    background: on ? 'var(--ew-green)' : 'var(--ew-white)',
                    color: on ? '#fff' : 'var(--ew-n-500)',
                  }}
                >
                  {d.short}
                </button>
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {DOW_PRESETS.map((preset) => {
              const active = dowsMatch(preset.dows);
              return (
                <button
                  key={preset.label}
                  type="button"
                  onClick={() => setDows(new Set(preset.dows))}
                  style={{
                    padding: '3px 9px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    borderRadius: 'var(--ew-r-pill)',
                    border: `1px solid ${active ? 'var(--ew-green)' : 'var(--ew-n-200)'}`,
                    background: active ? 'var(--ew-green-50)' : 'var(--ew-white)',
                    color: active ? 'var(--ew-green-700)' : 'var(--ew-n-500)',
                  }}
                >
                  {preset.label}
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <span style={lbl}>{isFab ? 'Crew per day, by phase' : 'Crew per day'}</span>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            {crewRows.map((r) => (
              <label key={r.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--ew-n-700)' }}>
                {isFab && (
                  <span style={{ minWidth: 78, fontWeight: 600 }}>
                    {r.phase === 'fabrication' ? 'Fabrication' : 'Installation'}
                  </span>
                )}
                <input
                  type="number" min={0} max={999} inputMode="numeric"
                  value={counts[r.key]}
                  onChange={(e) => setCounts((c) => ({ ...c, [r.key]: e.target.value }))}
                  placeholder="—"
                  aria-label={isFab ? `${r.phase} crew per day` : 'Crew per day'}
                  style={{ ...inp, width: 76 }}
                />
              </label>
            ))}
          </div>
          {isFab && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--ew-n-400)' }}>
              Leave a phase blank to skip it.
            </div>
          )}
        </div>

        {/* What this will actually do — shown before the button, not after. */}
        <div style={{ padding: '10px 12px', marginBottom: 14, borderRadius: 'var(--ew-r-sm)', background: 'var(--ew-n-50)', border: '1px solid var(--ew-n-200)', fontSize: 12, color: 'var(--ew-n-700)', lineHeight: 1.5 }}>
          {writeDays.length > 0 ? (<>
            <div>
              <b>{writeDays.length}</b> day{writeDays.length === 1 ? '' : 's'} — {dows.size} working day{dows.size === 1 ? '' : 's'} a week.
            </div>
            {/* The range legitimately starts in the past on a job already under
                way (it defaults to the phase span), so this is a normal state to
                explain rather than an error to flag. */}
            {plan.skippedPastDays > 0 && (
              <div style={{ color: 'var(--ew-n-500)', marginTop: 2 }}>
                Starts today — {plan.skippedPastDays} working day{plan.skippedPastDays === 1 ? '' : 's'} earlier in the range {plan.skippedPastDays === 1 ? 'has' : 'have'} already passed and {plan.skippedPastDays === 1 ? 'is' : 'are'} left as {plan.skippedPastDays === 1 ? 'it is' : 'they are'}.
              </div>
            )}
            {existing.isPending && rangeValid && (
              <div style={{ color: 'var(--ew-n-500)', marginTop: 2 }}>Checking for existing crew…</div>
            )}
            {existing.isError && (
              <div style={{ color: 'var(--ew-danger)', marginTop: 2 }}>
                Couldn’t check for existing crew — staging will still replace whatever is there.
              </div>
            )}
            {existing.isSuccess && replacedCount > 0 && (
              <div style={{ color: 'var(--ew-performance)', fontWeight: 600, marginTop: 2 }}>
                ⚠ Replaces crew already on {replacedCount} day cell{replacedCount === 1 ? '' : 's'} — including any outside the dates on screen.
              </div>
            )}
            {/* The clearing behaviour is the part a scheduler can't see coming,
                so it gets its own line with the count, not a footnote. */}
            {existing.isSuccess && clearedCells.length > 0 && (
              <div style={{ color: 'var(--ew-performance)', fontWeight: 600, marginTop: 2 }}>
                ⚠ Clears crew from {clearedCells.length} other upcoming day cell{clearedCells.length === 1 ? '' : 's'} on
                {isFab ? ' these lines' : ' this job'} — days the new pattern doesn’t cover.
              </div>
            )}
            {existing.isSuccess && replacedCount === 0 && clearedCells.length === 0 && (
              <div style={{ color: 'var(--ew-n-500)', marginTop: 2 }}>No existing crew to replace or clear.</div>
            )}
            <div style={{ marginTop: 4, color: 'var(--ew-n-500)' }}>
              Sets the job’s schedule from today on: working days get the crew size, anything
              else already scheduled ahead is cleared. Nothing before today is changed.
            </div>
            <div style={{ marginTop: 4, color: 'var(--ew-n-500)' }}>
              Stages as a draft — nobody sees it until you Publish, and Reset changes undoes it.
            </div>
          </>) : (
            <div style={{ color: 'var(--ew-n-500)' }}>Choose a date range to see what will be written.</div>
          )}
        </div>

        {problem && (
          <div style={{ marginBottom: 12, fontSize: 12, fontWeight: 600, color: 'var(--ew-danger)' }}>{problem}</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" style={{ fontSize: 12, padding: '6px 12px' }} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" style={{ fontSize: 12, padding: '6px 14px' }} onClick={apply} disabled={!!problem || busy}>
            {busy ? 'Staging…' : 'Stage crew'}
          </button>
        </div>
      </div>
    </div>
  );
}
